import * as React from "react";
import {
  UploadCloud,
  FileText,
  Image as ImageIcon,
  FileCode,
  Film,
  Music,
  Eye,
  Download,
  Trash2,
  X,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./dialog";

// Session-scoped cache so files uploaded in the current browser session retain
// their instant in-memory blob/data URL and text content even after parent queries
// replace the file list with backend attachment records or after a page reload.
const sessionPreviewCache = new Map();
const SESSION_STORAGE_PREFIX = "homy:file-preview:";

function rememberPreview(keys, entry) {
  keys.filter(Boolean).forEach((key) => {
    const k = String(key);
    sessionPreviewCache.set(k, entry);
    if (entry?.dataUrl || entry?.textContent) {
      try {
        sessionStorage.setItem(
          `${SESSION_STORAGE_PREFIX}${k}`,
          JSON.stringify({
            dataUrl: entry.dataUrl || null,
            textContent: entry.textContent || null,
          })
        );
      } catch {
        // Ignore storage quota errors
      }
    }
  });
}

function lookupPreview(file) {
  const candidates = [
    file.id,
    file.assetId,
    file.asset_id,
    file.name && file.size != null ? `${file.name}:${file.size}` : null,
    file.name,
  ].filter(Boolean);

  for (const key of candidates) {
    const k = String(key);
    const hit = sessionPreviewCache.get(k);
    if (hit) return hit;
    try {
      const raw = sessionStorage.getItem(`${SESSION_STORAGE_PREFIX}${k}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.dataUrl || parsed?.textContent) {
          const restored = {
            blobUrl: parsed.dataUrl || null,
            dataUrl: parsed.dataUrl || null,
            textContent: parsed.textContent || null,
          };
          sessionPreviewCache.set(k, restored);
          return restored;
        }
      }
    } catch {
      // Ignore storage read errors
    }
  }
  return null;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve) => {
    if (typeof FileReader === "undefined" || file.size > 2 * 1024 * 1024) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes, decimals = 1) {
  if (bytes === undefined || bytes === null || Number.isNaN(Number(bytes))) {
    return "—";
  }
  const num = Number(bytes);
  if (num === 0) return "0 B";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return `${parseFloat((num / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function getPreviewKind(file) {
  const name = (file?.name || "").toLowerCase();
  const type = (file?.type || "").toLowerCase();
  const url = (file?.url || "").toLowerCase().split("?")[0];

  if (
    type.startsWith("image/") ||
    /\.(jpeg|jpg|png|webp|gif|svg|bmp|avif)$/i.test(name) ||
    /\.(jpeg|jpg|png|webp|gif|svg|bmp|avif)$/i.test(url)
  ) {
    return "image";
  }
  if (
    type === "application/pdf" ||
    /\.pdf$/i.test(name) ||
    /\.pdf$/i.test(url)
  ) {
    return "pdf";
  }
  if (
    type.startsWith("text/") ||
    type === "application/json" ||
    type === "application/xml" ||
    /\.(txt|md|csv|json|xml|log|yml|yaml)$/i.test(name)
  ) {
    return "text";
  }
  if (
    type.startsWith("video/") ||
    /\.(mp4|webm|mov|ogg)$/i.test(name)
  ) {
    return "video";
  }
  if (
    type.startsWith("audio/") ||
    /\.(mp3|wav|ogg|m4a)$/i.test(name)
  ) {
    return "audio";
  }
  return null;
}

function normalizeFileItem(raw) {
  if (!raw) return null;
  const name =
    raw.name ||
    raw.originalFilename ||
    raw.original_filename ||
    raw.filename ||
    "attachment";
  const size =
    raw.size ?? raw.fileSize ?? raw.file_size ?? null;
  const type =
    raw.type || raw.contentType || raw.content_type || "application/octet-stream";
  const assetId =
    raw.assetId ||
    raw.asset_id ||
    (raw.s3Key || raw.s3_key || raw.uploadStatus || raw.upload_status ? raw.id : null);
  const id = raw.id || assetId || name;
  const cached = lookupPreview({ id, assetId, name, size });
  const rawUrl = raw.url || raw.downloadUrl || raw.download_url || null;
  // Prefer local blob URL from current session for instant zero-download preview
  const previewUrl = cached?.blobUrl || rawUrl;
  const badge =
    raw.badge ||
    raw.documentType ||
    raw.document_type ||
    raw.description ||
    raw.tags?.label ||
    raw.tags?.category ||
    null;

  return {
    id,
    assetId,
    name,
    size,
    type,
    badge,
    url: previewUrl,
    downloadUrl: rawUrl || cached?.blobUrl || null,
    textContent: raw.textContent ?? cached?.textContent ?? null,
    status: raw.status || "completed",
    error: raw.error || null,
    rawFile: raw.rawFile || null,
  };
}

function matchesAccept(file, accept) {
  if (!accept || accept === "*") return true;
  const acceptedTypes = accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  const fileName = file.name.toLowerCase();
  const fileType = (file.type || "").toLowerCase();

  return acceptedTypes.some((type) => {
    if (type === "*") return true;
    if (type.startsWith(".")) {
      return fileName.endsWith(type);
    }
    if (type.endsWith("/*")) {
      const baseType = type.slice(0, -2);
      return fileType.startsWith(baseType);
    }
    return fileType === type;
  });
}

function formatAcceptLabel(accept) {
  if (!accept || accept === "*") return "all files";
  return accept
    .split(",")
    .map((t) => {
      const trimmed = t.trim();
      if (trimmed === "application/pdf" || trimmed === ".pdf") return "PDF";
      if (trimmed === "image/*") return "Images";
      return trimmed
        .replace(/^application\//, "")
        .replace(/^image\//, "")
        .toUpperCase();
    })
    .join(", ");
}

const FileDropzone = React.forwardRef(
  (
    {
      accept = "application/pdf,image/*",
      maxSizeMB = 5,
      maxFiles = 20,
      multiple = false,
      value,
      attachments,
      loading = false,
      showEmptyState = false,
      compact = false,
      onUpload,
      onFilesSelected,
      onRemove,
      onResolvePreviewUrl,
      disabled = false,
      className,
      promptTitle = "Drag and drop file here",
      promptSubtitle,
      browseLabel = "Browse files",
      uploadingLabel = "Uploading...",
      readyLabel = "Ready",
      failedLabel = "Failed",
      previewLabel = "Preview",
      downloadLabel = "Download",
      removeLabel = "Remove file",
      closeLabel = "Close",
      emptyLabel = "No files attached yet",
      previewUnavailableLabel = "Inline preview is not available for this file.",
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [, setDragCounter] = React.useState(0);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [internalFiles, setInternalFiles] = React.useState([]);
    const [inFlightFiles, setInFlightFiles] = React.useState([]);
    const [previewItem, setPreviewItem] = React.useState(null);
    const [previewResolvedUrl, setPreviewResolvedUrl] = React.useState(null);
    const [previewTextContent, setPreviewTextContent] = React.useState(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [imageLoadFailed, setImageLoadFailed] = React.useState(false);

    const controlledSource = attachments !== undefined ? attachments : value;
    const isControlled = controlledSource !== undefined;

    const files = React.useMemo(() => {
      if (!isControlled) {
        return internalFiles.map(normalizeFileItem).filter(Boolean);
      }
      const persisted = (controlledSource || [])
        .map(normalizeFileItem)
        .filter(Boolean);
      const persistedKeys = new Set(
        persisted.flatMap((f) => [
          f.id,
          f.assetId,
          f.name && f.size != null ? `${f.name}:${f.size}` : null,
        ].filter(Boolean))
      );
      const activeInFlight = inFlightFiles
        .filter(
          (f) =>
            !persistedKeys.has(f.id) &&
            (!f.assetId || !persistedKeys.has(f.assetId)) &&
            (!f.name || f.size == null || !persistedKeys.has(`${f.name}:${f.size}`))
        )
        .map(normalizeFileItem)
        .filter(Boolean);
      return [...activeInFlight, ...persisted];
    }, [isControlled, controlledSource, internalFiles, inFlightFiles]);

    const subtitle =
      promptSubtitle ||
      `Supports ${formatAcceptLabel(accept)} up to ${maxSizeMB}MB`;

    const handleFiles = React.useCallback(
      async (incomingFileList) => {
        if (disabled) return;
        setErrorMessage(null);

        const newFilesArray = Array.from(incomingFileList);
        let validFiles = [];

        for (const file of newFilesArray) {
          if (file.size > maxSizeMB * 1024 * 1024) {
            setErrorMessage(
              `File "${file.name}" exceeds maximum allowed size of ${maxSizeMB}MB.`
            );
            return;
          }

          if (!matchesAccept(file, accept)) {
            setErrorMessage(
              `File "${file.name}" does not match accepted format (${accept}).`
            );
            return;
          }

          validFiles.push(file);
        }

        if (validFiles.length === 0) return;

        if (multiple && maxFiles) {
          const currentCount = files.length;
          const remainingSlots = Math.max(0, maxFiles - currentCount);
          if (remainingSlots <= 0) {
            setErrorMessage(
              `You can upload at most ${maxFiles} file${maxFiles > 1 ? "s" : ""}.`
            );
            return;
          }
          if (validFiles.length > remainingSlots) {
            validFiles = validFiles.slice(0, remainingSlots);
          }
        }

        const filesToProcess = multiple ? validFiles : validFiles.slice(0, 1);
        onFilesSelected?.(filesToProcess);

        for (const file of filesToProcess) {
          const fileId = `${file.name}-${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 7)}`;
          const kind = getPreviewKind({ name: file.name, type: file.type });
          const blobUrl =
            typeof URL !== "undefined" && URL.createObjectURL
              ? URL.createObjectURL(file)
              : undefined;

          let textContent = null;
          if (kind === "text" && typeof file.text === "function" && file.size <= 512 * 1024) {
            try {
              textContent = await file.text();
            } catch {
              textContent = null;
            }
          }

          let dataUrl = null;
          if (!blobUrl && (kind === "image" || kind === "pdf")) {
            dataUrl = await readFileAsDataUrl(file);
          }

          rememberPreview(
            [fileId, `${file.name}:${file.size}`, file.name],
            { blobUrl: blobUrl || dataUrl, dataUrl, textContent }
          );

          const fileEntry = {
            id: fileId,
            name: file.name,
            size: file.size,
            type: file.type || "application/octet-stream",
            url: blobUrl || dataUrl,
            downloadUrl: blobUrl || dataUrl,
            textContent,
            status: onUpload ? "uploading" : "completed",
            rawFile: file,
          };

          if (isControlled) {
            setInFlightFiles((prev) => (multiple ? [...prev, fileEntry] : [fileEntry]));
          } else if (multiple) {
            setInternalFiles((prev) => [...prev, fileEntry]);
          } else {
            setInternalFiles([fileEntry]);
          }

          if (onUpload) {
            try {
              const res = await onUpload(file);
              const returnedId = res?.id || res?.attachmentId || null;
              const returnedAssetId = res?.assetId || res?.asset_id || null;
              const returnedUrl =
                res?.url || res?.downloadUrl || res?.download_url || blobUrl || dataUrl;

              rememberPreview(
                [
                  fileId,
                  returnedId,
                  returnedAssetId,
                  `${file.name}:${file.size}`,
                  file.name,
                ],
                { blobUrl: blobUrl || dataUrl || returnedUrl, dataUrl, textContent }
              );

              if (isControlled) {
                // Remove from in-flight once parent controlled list updates
                setInFlightFiles((prev) => prev.filter((item) => item.id !== fileId));
              } else {
                setInternalFiles((prev) =>
                  prev.map((item) =>
                    item.id === fileId
                      ? {
                          ...item,
                          id: returnedId || item.id,
                          status: "completed",
                          assetId: returnedAssetId,
                          url: blobUrl || returnedUrl,
                          downloadUrl: returnedUrl || blobUrl,
                        }
                      : item
                  )
                );
              }
            } catch (err) {
              const updateErr = (prev) =>
                prev.map((item) =>
                  item.id === fileId
                    ? {
                        ...item,
                        status: "error",
                        error: err?.message || "Upload failed",
                      }
                    : item
                );
              if (isControlled) {
                setInFlightFiles(updateErr);
              } else {
                setInternalFiles(updateErr);
              }
              setErrorMessage(err?.message || `Failed to upload ${file.name}`);
            }
          }
        }
      },
      [
        disabled,
        maxSizeMB,
        accept,
        files.length,
        multiple,
        maxFiles,
        onFilesSelected,
        onUpload,
        isControlled,
      ]
    );

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev + 1);
      if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => {
        const next = prev - 1;
        if (next <= 0) setIsDragging(false);
        return Math.max(0, next);
      });
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragCounter(0);
      if (disabled) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    };

    const handleRemove = (fileToRemove) => {
      if (disabled) return;
      const idToRemove = fileToRemove.id;
      onRemove?.(idToRemove, fileToRemove);
      setInternalFiles((prev) => prev.filter((item) => item.id !== idToRemove));
      setInFlightFiles((prev) => prev.filter((item) => item.id !== idToRemove));
      if (previewItem?.id === idToRemove) {
        setPreviewItem(null);
      }
    };

    const handleOpenPreview = async (file) => {
      const kind = getPreviewKind(file);
      if (!kind) return;
      setImageLoadFailed(false);
      setPreviewItem({ ...file, previewKind: kind });
      setPreviewTextContent(file.textContent || null);

      let targetUrl = file.url || file.downloadUrl || null;
      if (onResolvePreviewUrl) {
        try {
          setPreviewLoading(true);
          const resolved = await onResolvePreviewUrl(file);
          if (resolved) {
            targetUrl = resolved;
          }
        } catch {
          // Keep existing targetUrl on error
        } finally {
          setPreviewLoading(false);
        }
      }
      setPreviewResolvedUrl(targetUrl);

      if (kind === "text" && !file.textContent && targetUrl) {
        try {
          setPreviewLoading(true);
          const resp = await fetch(targetUrl);
          if (resp.ok) {
            const txt = await resp.text();
            setPreviewTextContent(txt);
          }
        } catch {
          // Ignore fetch failure
        } finally {
          setPreviewLoading(false);
        }
      }
    };

    const handleDownload = async (file) => {
      let targetUrl = file.downloadUrl || file.url || null;
      if ((!targetUrl || targetUrl.startsWith("/api/")) && onResolvePreviewUrl) {
        try {
          targetUrl = await onResolvePreviewUrl(file);
        } catch {
          // Ignore error
        }
      }
      if (!targetUrl) return;
      const a = document.createElement("a");
      a.href = targetUrl;
      a.download = file.name || "download";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    const effectiveMaxFiles = multiple ? maxFiles : 1;
    const canUploadMore = files.length < effectiveMaxFiles;
    const isDropzoneDisabled = Boolean(disabled || !canUploadMore);

    const openFileDialog = () => {
      if (!isDropzoneDisabled && inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.click();
      }
    };

    const renderFileIcon = (file, kind) => {
      const isImage = kind === "image";
      if (isImage && file.url && !file.url.startsWith("/api/")) {
        return (
          <img
            src={file.url}
            alt={file.name}
            className="size-full object-cover"
          />
        );
      }
      if (isImage) return <ImageIcon className="size-5 text-info" />;
      if (kind === "text") return <FileCode className="size-5 text-success" />;
      if (kind === "video") return <Film className="size-5 text-primary" />;
      if (kind === "audio") return <Music className="size-5 text-primary" />;
      return <FileText className="size-5 text-warning" />;
    };

    return (
      <div
        ref={ref}
        className={cn("space-y-3", className)}
        data-testid="file-dropzone"
        {...props}
      >
        {/* Drop Target Area */}
        <div
          role="button"
          tabIndex={isDropzoneDisabled ? -1 : 0}
          aria-label={promptTitle}
          aria-disabled={isDropzoneDisabled}
          onKeyDown={(e) => {
            if (!isDropzoneDisabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              openFileDialog();
            }
          }}
          onDragEnter={!isDropzoneDisabled ? handleDragEnter : undefined}
          onDragLeave={!isDropzoneDisabled ? handleDragLeave : undefined}
          onDragOver={!isDropzoneDisabled ? handleDragOver : undefined}
          onDrop={!isDropzoneDisabled ? handleDrop : undefined}
          onClick={!isDropzoneDisabled ? openFileDialog : undefined}
          data-dragging={isDragging}
          data-testid="file-dropzone-target"
          className={cn(
            "group relative flex items-center transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            compact
              ? "flex-row justify-between rounded-xl border border-dashed px-3 py-2 min-h-[40px] gap-2"
              : "flex-col justify-center rounded-2xl border-2 border-dashed p-5 text-center min-h-[132px]",
            isDragging
              ? "border-primary bg-primary/10 ring-4 ring-primary/20"
              : "border-border bg-muted/20 hover:border-primary/40 hover:bg-muted/40",
            isDropzoneDisabled &&
              "cursor-not-allowed opacity-50 bg-muted/10 pointer-events-none"
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={isDropzoneDisabled}
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
            className="sr-only"
            data-testid="file-dropzone-input"
          />

          {compact ? (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <UploadCloud className="size-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="text-xs font-medium text-foreground truncate">
                  {promptTitle}
                </span>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-caption font-medium bg-background text-foreground border border-border group-hover:bg-muted transition-colors shrink-0">
                {browseLabel}
              </span>
            </>
          ) : (
            <>
              <div className="flex size-10 items-center justify-center rounded-full bg-background border border-border text-muted-foreground group-hover:text-primary group-hover:border-primary/30 transition-colors mb-2 shadow-2xs">
                <UploadCloud className="size-5" />
              </div>

              <p className="text-sm font-semibold text-foreground tracking-tight">
                {promptTitle}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>

              <button
                type="button"
                disabled={isDropzoneDisabled}
                className="mt-2.5 inline-flex items-center justify-center min-h-[44px] px-3.5 py-2 rounded-lg text-xs font-medium bg-background text-foreground border border-border hover:bg-muted transition-colors cursor-pointer shadow-2xs"
              >
                {browseLabel}
              </button>
            </>
          )}
        </div>

        {/* Error Notification */}
        {errorMessage && (
          <div className="flex items-center gap-2 rounded-xl bg-destructive/10 border border-destructive/20 px-3 py-2 text-xs text-destructive">
            <AlertCircle className="size-4 shrink-0" />
            <span className="flex-1">{errorMessage}</span>
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 text-destructive/80 hover:text-destructive cursor-pointer rounded-lg -mr-1"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="h-16 w-full animate-pulse rounded-xl bg-muted/40 border border-border" />
        )}

        {/* Empty State */}
        {!loading && showEmptyState && files.length === 0 && (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        )}

        {/* Unified Selected / Uploaded Files & Attachments List */}
        {!loading && files.length > 0 && (
          <div className="space-y-2" data-testid="file-dropzone-list">
            {files.map((file) => {
              const previewKind = getPreviewKind(file);
              const isPreviewable = Boolean(previewKind);
              const isUploading = file.status === "uploading";
              const isError = file.status === "error";
              const downloadHref = file.downloadUrl || file.url;
              const canDownload = Boolean(downloadHref || file.assetId);

              return (
                <div
                  key={file.id}
                  data-testid="file-dropzone-item"
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-sm shadow-2xs hover:bg-muted/30 transition-all"
                >
                  {/* File Thumbnail or Icon */}
                  <button
                    type="button"
                    disabled={!isPreviewable || isUploading}
                    onClick={() => isPreviewable && handleOpenPreview(file)}
                    title={isPreviewable ? `${previewLabel}: ${file.name}` : file.name}
                    className={cn(
                      "flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted border border-border overflow-hidden text-muted-foreground transition-colors",
                      isPreviewable && !isUploading
                        ? "cursor-pointer hover:border-primary/50 hover:ring-2 hover:ring-primary/20"
                        : "cursor-default"
                    )}
                  >
                    {renderFileIcon(file, previewKind)}
                  </button>

                  {/* Metadata */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {isPreviewable && !isUploading ? (
                        <button
                          type="button"
                          onClick={() => handleOpenPreview(file)}
                          className="truncate text-left font-medium text-xs text-foreground hover:text-primary transition-colors cursor-pointer leading-tight"
                          title={`${previewLabel}: ${file.name}`}
                        >
                          {file.name}
                        </button>
                      ) : (
                        <p className="truncate font-medium text-xs text-foreground leading-tight">
                          {file.name}
                        </p>
                      )}
                      {file.badge && (
                        <span
                          data-testid="file-dropzone-badge"
                          className="inline-flex items-center shrink-0 rounded-md bg-muted px-2 py-0.5 text-overline-size font-medium text-muted-foreground border border-border/80"
                        >
                          {file.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-caption text-muted-foreground">
                      <span className="font-mono tabular-nums">
                        {formatBytes(file.size)}
                      </span>
                      {isUploading && (
                        <span className="inline-flex items-center gap-1 text-info">
                          <Loader2 className="size-3 animate-spin" />
                          {uploadingLabel}
                        </span>
                      )}
                      {!isUploading && !isError && (
                        <span className="inline-flex items-center gap-1 text-success">
                          <CheckCircle2 className="size-3" />
                          {readyLabel}
                        </span>
                      )}
                      {isError && (
                        <span className="inline-flex items-center gap-1 text-destructive">
                          <AlertCircle className="size-3" />
                          {failedLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions: Preview / Download / Remove */}
                  <div className="flex items-center gap-1 shrink-0">
                    {isPreviewable && !isUploading && (
                      <button
                        type="button"
                        onClick={() => handleOpenPreview(file)}
                        data-testid="file-dropzone-preview-btn"
                        className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                        title={previewLabel}
                      >
                        <Eye className="size-4" />
                        <span className="sr-only">{previewLabel}</span>
                      </button>
                    )}

                    {canDownload && !isUploading && (
                      downloadHref && !downloadHref.startsWith("/api/") ? (
                        <a
                          href={downloadHref}
                          download={file.name}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="file-dropzone-download-btn"
                          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          title={downloadLabel}
                        >
                          <Download className="size-4" />
                          <span className="sr-only">{downloadLabel}</span>
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDownload(file)}
                          data-testid="file-dropzone-download-btn"
                          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          title={downloadLabel}
                        >
                          <Download className="size-4" />
                          <span className="sr-only">{downloadLabel}</span>
                        </button>
                      )
                    )}

                    {!disabled && onRemove && (
                      <button
                        type="button"
                        onClick={() => handleRemove(file)}
                        data-testid="file-dropzone-remove-btn"
                        className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] h-11 w-11 rounded-lg text-muted-foreground hover:bg-muted hover:text-destructive transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                        title={removeLabel}
                      >
                        <Trash2 className="size-4" />
                        <span className="sr-only">{removeLabel}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Inline File Preview Modal */}
        <Dialog
          open={Boolean(previewItem)}
          onOpenChange={(open) => {
            if (!open) {
              setPreviewItem(null);
              setPreviewResolvedUrl(null);
              setPreviewTextContent(null);
              setImageLoadFailed(false);
            }
          }}
        >
          <DialogContent
            closeLabel={closeLabel}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="max-w-3xl w-[92vw] p-0 overflow-hidden border-border bg-card"
            data-testid="file-dropzone-preview-modal"
          >
            {previewItem && (
              <div className="flex flex-col">
                <DialogHeader className="flex flex-row items-center justify-between gap-3 border-b border-border px-5 py-4 pr-16 text-left space-y-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted border border-border">
                      {renderFileIcon(previewItem, previewItem.previewKind)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <DialogTitle className="truncate text-sm font-semibold text-foreground">
                          {previewItem.name}
                        </DialogTitle>
                        {previewItem.badge && (
                          <span className="inline-flex items-center shrink-0 rounded-md bg-muted px-2 py-0.5 text-overline-size font-medium text-muted-foreground border border-border/80">
                            {previewItem.badge}
                          </span>
                        )}
                      </div>
                      <DialogDescription className="text-xs text-muted-foreground mt-0.5 font-mono tabular-nums">
                        {formatBytes(previewItem.size)}
                        {previewItem.type ? ` • ${previewItem.type}` : ""}
                      </DialogDescription>
                    </div>
                  </div>

                  {(previewResolvedUrl || previewItem.downloadUrl || previewItem.url) && (
                    <a
                      href={previewResolvedUrl || previewItem.downloadUrl || previewItem.url}
                      download={previewItem.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                    >
                      <Download className="size-3.5" />
                      {downloadLabel}
                    </a>
                  )}
                </DialogHeader>

                <div className="p-5 bg-muted/20 flex items-center justify-center min-h-[260px] max-h-[75vh] overflow-auto">
                  {previewLoading ? (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground py-12">
                      <Loader2 className="size-6 animate-spin text-primary" />
                    </div>
                  ) : previewItem.previewKind === "image" &&
                    previewResolvedUrl &&
                    !imageLoadFailed ? (
                    <img
                      src={previewResolvedUrl}
                      alt={previewItem.name}
                      onError={() => setImageLoadFailed(true)}
                      data-testid="file-dropzone-preview-image"
                      className="max-h-[68vh] w-auto max-w-full object-contain rounded-lg border border-border bg-background shadow-sm"
                    />
                  ) : previewItem.previewKind === "pdf" &&
                    previewResolvedUrl &&
                    !previewResolvedUrl.startsWith("/api/") ? (
                    <iframe
                      src={previewResolvedUrl}
                      title={previewItem.name}
                      data-testid="file-dropzone-preview-pdf"
                      className="w-full h-[68vh] rounded-lg border border-border bg-background"
                    />
                  ) : previewItem.previewKind === "text" &&
                    previewTextContent !== null ? (
                    <pre
                      data-testid="file-dropzone-preview-text"
                      className="w-full max-h-[65vh] overflow-auto rounded-lg border border-border bg-background p-4 text-xs font-mono text-foreground whitespace-pre-wrap break-words"
                    >
                      {previewTextContent}
                    </pre>
                  ) : previewItem.previewKind === "video" &&
                    previewResolvedUrl ? (
                    <video
                      src={previewResolvedUrl}
                      controls
                      data-testid="file-dropzone-preview-video"
                      className="max-h-[68vh] w-full rounded-lg border border-border bg-black"
                    />
                  ) : previewItem.previewKind === "audio" &&
                    previewResolvedUrl ? (
                    <audio
                      src={previewResolvedUrl}
                      controls
                      data-testid="file-dropzone-preview-audio"
                      className="w-full max-w-md"
                    />
                  ) : (
                    <div
                      data-testid="file-dropzone-preview-fallback"
                      className="flex flex-col items-center justify-center text-center py-10 px-4 gap-2 text-muted-foreground"
                    >
                      <FileText className="size-10 text-muted-foreground/60" />
                      <p className="text-sm font-medium text-foreground">
                        {previewItem.name}
                      </p>
                      <p className="text-xs text-muted-foreground max-w-sm">
                        {previewUnavailableLabel}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);
FileDropzone.displayName = "FileDropzone";

export { FileDropzone, formatBytes };
